import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { formatDate } from '../lib/money';
import type { Recipient, RecipientInput } from '../lib/whatsapp';
import { whatsappApi } from '../lib/whatsapp';

/**
 * Settings → WhatsApp reminders (31 October 2026).
 *
 * At 08:00, on any day something on "Expiring soon" is due, the list goes to
 * everyone here through TextMeBot. On its $1 plan that is one number — the
 * WhatsApp linked to the TextMeBot account — with that account's key. The editor opens under the person tapped, like the
 * plant editor. Loads its own list, so Settings does not wait on it.
 */
export default function WhatsAppCard() {
  const [people, setPeople] = useState<Recipient[] | null>(null);
  const [editing, setEditing] = useState<Recipient | 'new' | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [error, setError] = useState('');

  async function refresh() {
    try {
      setPeople((await whatsappApi.list()).recipients);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the WhatsApp list.');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function act(work: () => Promise<unknown>) {
    setError('');
    try {
      await work();
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
      throw err;
    }
  }

  async function test(person: Recipient) {
    setError('');
    setTesting(person.id);
    try {
      const { recipient } = await whatsappApi.test(person.id);
      setPeople((list) => list?.map((p) => (p.id === recipient.id ? recipient : p)) ?? null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send the test.');
    } finally {
      setTesting(null);
    }
  }

  const editor = (person: Recipient | null) => (
    <RecipientEditor
      key={person?.id ?? 'new'}
      person={person}
      onCancel={() => setEditing(null)}
      onSave={(body) => act(() => (person ? whatsappApi.update(person.id, { ...body, rowVersion: person.rowVersion }) : whatsappApi.add(body)))}
      onRemove={person ? () => act(() => whatsappApi.remove(person.id)) : null}
    />
  );

  return (
    <div className="card">
      <h3>WhatsApp reminders</h3>
      <div className="sub">
        At 8:00 each morning, if a permit, vehicle paper or plant service is due, the list on
        Approvals is sent on WhatsApp to the number here, through TextMeBot. Add the WhatsApp
        number you linked to TextMeBot and the API key from your TextMeBot account.
      </div>

      {error && <div className="banner b-no" style={{ marginTop: 8 }}>{error}</div>}
      {people === null && !error && <div className="empty">Loading…</div>}
      {people?.length === 0 && <div className="empty">Nobody yet — no WhatsApp is sent.</div>}

      {people?.map((person) => {
        const open = editing !== null && editing !== 'new' && editing.id === person.id;
        return (
          <div key={person.id}>
            <div className="row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="name">{person.name}</div>
                <div className="sub">
                  {person.phone} · key …{person.keyEnd}
                  {person.lastSentAt && (
                    <>
                      {' · '}
                      {person.lastResult === 'sent' ? 'last sent ' : 'last try '}
                      {formatDate(person.lastSentAt.slice(0, 10))}{' '}
                      {new Date(person.lastSentAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                    </>
                  )}
                </div>
                {person.lastResult && person.lastResult !== 'sent' && (
                  <div className="sub" style={{ color: 'var(--text-danger)' }}>
                    Not sent — {person.lastResult}. Check the number and the key.
                  </div>
                )}
              </div>
              {person.lastResult === 'sent' && <span className="pill p-ok">sent</span>}
              {!open && (
                <>
                  <button
                    type="button"
                    className="btn sm"
                    style={{ minWidth: 0 }}
                    disabled={testing !== null}
                    onClick={() => void test(person)}
                  >
                    {testing === person.id ? 'Sending…' : 'Send a test'}
                  </button>
                  <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => setEditing(person)}>
                    Edit ›
                  </button>
                </>
              )}
            </div>
            {open && editor(person)}
          </div>
        );
      })}

      {editing === null && (
        <div className="bar" style={{ marginTop: 12 }}>
          <button type="button" className="btn pri" onClick={() => setEditing('new')}>
            Add a person
          </button>
        </div>
      )}
      {editing === 'new' && editor(null)}
    </div>
  );
}

function RecipientEditor({
  person, onCancel, onSave, onRemove,
}: {
  person: Recipient | null;
  onCancel: () => void;
  onSave: (body: RecipientInput) => Promise<void>;
  onRemove: (() => Promise<void>) | null;
}) {
  const [name, setName] = useState(person?.name ?? '');
  const [phone, setPhone] = useState(person?.phone ?? '');
  const [apikey, setApikey] = useState('');
  const [saving, setSaving] = useState(false);
  const needsKey = person === null && apikey.trim().length === 0;

  return (
    <div className="card" style={{ borderColor: 'var(--accent)' }}>
      <h3 style={{ marginBottom: 10 }}>{person ? 'Edit person' : 'Add a person'}</h3>

      <div className="sub" style={{ marginBottom: 4 }}>Name</div>
      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Owner" />

      <div className="sub" style={{ margin: '10px 0 4px' }}>WhatsApp number</div>
      <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+94 77 123 4567" />

      <div className="sub" style={{ margin: '10px 0 4px' }}>TextMeBot API key</div>
      <input
        type="text"
        autoComplete="off"
        value={apikey}
        onChange={(e) => setApikey(e.target.value)}
        placeholder={person ? `Leave empty to keep the key ending …${person.keyEnd}` : 'From your TextMeBot account'}
      />

      <div className="bar" style={{ marginTop: 14 }}>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>Cancel</button>
        {onRemove && (
          <button
            type="button"
            className="btn dang"
            disabled={saving}
            onClick={() => { setSaving(true); void onRemove().catch(() => undefined).finally(() => setSaving(false)); }}
          >
            Remove
          </button>
        )}
        <button
          type="button"
          className="btn pri"
          disabled={saving || name.trim().length === 0 || phone.trim().length === 0 || needsKey}
          onClick={() => {
            setSaving(true);
            void onSave({ name: name.trim(), phone: phone.trim(), apikey: apikey.trim() })
              .catch(() => undefined)
              .finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : person ? 'Save' : 'Add'}
        </button>
      </div>
    </div>
  );
}
